import React from "react";
import VariantGate from './_variant_gate';
import { loginUser } from "../services/balanceApi";
import { consumeSessionExpired, setLoginSession } from "../utils/auth";
import LoginPageV2 from '../components/login_v2/login_v2';
import LoginPageV3 from '../components/login_v3/login_v3';

/**
 * 에러 표시 정책: 로그인 페이지는 form 내 inline 에러(setState({ error }))만 사용한다.
 * showToast 사용 금지 — 폼 컨텍스트 분리로 중복 알림 방지. console.error는 진단용으로 허용.
 */
class LoginPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            username: "",
            password: "",
            remember: true,
            error: "",
            isSubmitting: false,
            showPassword: false,
        };
    }

    componentDidMount() {
        const storedUsername = localStorage.getItem("username");
        const sessionExpired = consumeSessionExpired();
        this.setState({
            username: storedUsername || "",
            error: sessionExpired ? "세션이 만료되었습니다. 다시 로그인해주세요." : "",
        });
    }

    handleChange = (field) => (event) => {
        if (field === "remember") {
            // 커스텀 체크박스는 버튼으로 동작하므로 토글 처리
            this.setState(prev => ({ remember: !prev.remember }));
        } else {
            this.setState({ [field]: event.target.value });
        }
    };

    onSubmit = async (event) => {
        event.preventDefault();
        const { username, password } = this.state;
        if (!username || !password) {
            this.setState({ error: "아이디와 비밀번호를 입력하세요." });
            return;
        }

        this.setState({ isSubmitting: true, error: "" });
        try {
            const response = await loginUser(username, password);
            if (response.status === 200) {
                setLoginSession(username, response.data.access, response.data.refresh);
                window.location.href = "/dashboard";
            } else {
                this.setState({ error: "아이디 또는 비밀번호를 확인해주세요." });
            }
        } catch (error) {
            console.error("Error during login:", error);
            this.setState({ error: "로그인 중 오류가 발생했습니다." });
        } finally {
            this.setState({ isSubmitting: false });
        }
    };

    render() {
        const { username, password, remember, error, isSubmitting, showPassword } = this.state;

        return (
            <div className="relative flex min-h-screen items-center justify-center bg-surface px-4">
                {/* Background ambient glow */}
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                    <div className="absolute left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] rounded-full bg-brand-subtle blur-[120px] animate-pulse-soft" />
                    <div className="absolute left-1/3 top-2/3 -translate-x-1/2 -translate-y-1/2 h-[300px] w-[300px] rounded-full bg-gain-subtle blur-[100px] animate-pulse-soft" style={{ animationDelay: '1s' }} />
                    <div className="absolute right-1/4 top-1/4 h-[250px] w-[250px] rounded-full bg-loss-subtle blur-[80px] animate-pulse-soft" style={{ animationDelay: '2s' }} />
                </div>

                <div className="relative w-full max-w-xs sm:max-w-sm animate-fade-in">
                    {/* Logo (DAS-68/69: 공통 HexLogo 브랜드) */}
                    <div className="mb-8 flex flex-col items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl border-[1.5px] border-content">
                            <svg viewBox="0 0 48 48" fill="none" aria-hidden="true" className="h-7 w-7">
                                <polygon
                                    points="24,5 41,15 41,33 24,43 7,33 7,15"
                                    stroke="var(--color-content)"
                                    strokeWidth="3.5"
                                    strokeLinejoin="round"
                                    fill="none"
                                />
                                <path
                                    d="M15 30 L33 18"
                                    stroke="var(--color-brand)"
                                    strokeWidth="3.5"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </div>
                        <span className="text-sm font-bold tracking-wide text-content">MyDashboard</span>
                    </div>

                    {/* Card */}
                    <form
                        aria-label="로그인 폼"
                        className="bg-glass rounded-2xl border border-surface-border p-6 shadow-elevated space-y-5"
                        onSubmit={this.onSubmit}
                    >
                        {error && (
                            <div role="alert" className="rounded-lg bg-loss-subtle border border-loss/20 px-3 py-2.5 text-sm text-loss">
                                {error}
                            </div>
                        )}

                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-content-secondary uppercase tracking-wider" htmlFor="username">
                                Username
                            </label>
                            <input
                                id="username"
                                type="text"
                                value={username}
                                onChange={this.handleChange("username")}
                                className="w-full rounded-lg border border-surface-border bg-surface-elevated px-3.5 py-2.5 text-sm text-content placeholder-content-muted focus:border-brand focus:ring-1 focus:ring-brand/30 focus:outline-none transition-colors"
                                placeholder="사용자명을 입력하세요"
                            />
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-content-secondary uppercase tracking-wider" htmlFor="password">
                                Password
                            </label>
                            {/* 비밀번호 입력 + show/hide 토글 */}
                            <div className="relative">
                                <input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={this.handleChange("password")}
                                    className="w-full rounded-lg border border-surface-border bg-surface-elevated px-3.5 py-2.5 pr-10 text-sm text-content placeholder-content-muted focus:border-brand focus:ring-1 focus:ring-brand/30 focus:outline-none transition-colors"
                                    placeholder="비밀번호를 입력하세요"
                                />
                                <button
                                    type="button"
                                    onClick={() => this.setState(prev => ({ showPassword: !prev.showPassword }))}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-content-muted hover:text-content-secondary transition-colors"
                                    aria-label={showPassword ? "비밀번호 숨기기" : "비밀번호 보기"}
                                >
                                    {showPassword ? (
                                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                                            <line x1="1" y1="1" x2="23" y2="23" />
                                        </svg>
                                    ) : (
                                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* 커스텀 체크박스 - 아이디 기억하기 */}
                        <label className="flex items-center gap-2.5 text-sm text-content-secondary cursor-pointer select-none min-h-[44px] py-1">
                            <button
                                type="button"
                                role="checkbox"
                                aria-checked={remember}
                                onClick={this.handleChange("remember")}
                                className={`flex h-4 w-4 items-center justify-center rounded border transition-colors flex-shrink-0 ${
                                    remember
                                        ? "border-brand bg-brand"
                                        : "border-surface-border-light bg-surface-elevated"
                                }`}
                            >
                                {remember && (
                                    <svg className="h-3 w-3 text-content-inverse" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                        <path d="M20 6L9 17l-5-5" />
                                    </svg>
                                )}
                            </button>
                            아이디 기억하기
                        </label>

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full rounded-lg bg-gradient-brand px-4 py-2.5 text-sm font-semibold text-content-inverse shadow-glow-brand/30 transition-all hover:shadow-glow-brand hover:brightness-110 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:shadow-none"
                        >
                            {isSubmitting ? (
                                <span className="flex items-center justify-center gap-2">
                                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-content-inverse/30 border-t-content-inverse" />
                                    로그인 중...
                                </span>
                            ) : (
                                "로그인"
                            )}
                        </button>
                    </form>
                </div>
            </div>
        );
    }
}

// DAS-70 Wave-A: variant 슬롯 3-단 확장 (v1 기존 / v2 IS 리프레시 슬롯 / v3 정보 구조 재설계)
// v2 prop 은 Wave-B 에서 신규 IS 리프레시 컴포넌트로 채워진다. 현 시점에서는 null + fallbackToV1=true.
const LoginPageV1 = LoginPage;
export { LoginPageV1 };
const LoginPageWithVariant = (props) => (
    <VariantGate v1={LoginPageV1} v2={LoginPageV2} v3={LoginPageV3} fallbackToV1={true} {...props} />
);
export default LoginPageWithVariant;
